import { ExternalLinkIcon } from "@chakra-ui/icons"
import { Box, Button, Heading, Table, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react"
import { ContentBox } from "components/ContentBox"
import { SiteTitle } from "components/SiteTitle"
import { useBreadcrumb } from "components/breadcrumbs/BreadcrumbProvider"
import { useRatingReportHistoryQuery, useRegenerateReportMutation } from "lib/generated/graphql"
import { useReportDownload } from "lib/hooks/useReportDownload/useReportDownload"
import { useRating } from "lib/providers/RatingProvider"
import styled from "styled-components"

const Container = styled.div`
	flex: 1;
	display: flex;
	flex-flow: column nowrap;
	margin: 0 0 36px 0;
`

export const ReportManagement = () => {
  const { ratingId, ratingState } = useRating()
  const toast = useToast()
  useBreadcrumb("Rapport", `/ratings/${ratingId}/report`)

  const [historyResult, reexecuteQuery] = useRatingReportHistoryQuery({
    variables: { key: ratingId },
  })

  const [regenerateResult, regenerateReport] = useRegenerateReportMutation()

  const downloadReport = useReportDownload(ratingId)

  const handleRegenerate = async () => {
    const result = await regenerateReport({ ratingId })
    if (result.error) {
      toast({
        title: "Feil ved regenerering",
        description: result.error.message,
        status: "error",
        position: "top",
        isClosable: true,
      })
    } else {
      toast({
        title: "Rapport regenerert",
        description: "Ny rapport er generert. Det kan ta noen sekunder før den er tilgjengelig.",
        status: "success",
        position: "top",
        isClosable: true,
      })
      reexecuteQuery({ requestPolicy: "network-only" })
    }
  }

  const reports = historyResult.data?.ratingReportHistory ?? []

  if (ratingState.phase !== "COMPLETED") {
    return (
      <Container>
        <SiteTitle title={`Rapport - ${ratingState?.brreg?.navn}`} />
        <ContentBox>
          <Text>Rapporten er ikke tilgjengelig før målingen er fullført.</Text>
        </ContentBox>
      </Container>
    )
  }

  return (
    <Container>
      <SiteTitle title={`Rapport - ${ratingState?.brreg?.navn}`} />
      <ContentBox>
        <Box mb={6}>
          <Heading size="md" mb={2}>
            Rapportadministrasjon
          </Heading>
          <Text color="gray.600" mb={4}>
            Her kan du se alle genererte rapporter og regenerere rapporten dersom det er behov for en oppdatert versjon.
            Når en ny rapport genereres, vil alle eksisterende nedlastningslenker automatisk peke til den nye versjonen.
          </Text>
          <Button
            colorScheme="blue"
            onClick={handleRegenerate}
            isLoading={regenerateResult.fetching}
            loadingText="Genererer rapport..."
          >
            Regenerer rapport
          </Button>
        </Box>

        {reports.length > 0 && (
          <Box>
            <Heading size="sm" mb={3}>
              Rapporthistorikk
            </Heading>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Filnavn</Th>
                  <Th>Opprettet</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {[...reports].reverse().map((report) => (
                  <Tr key={report.pdfReportKey}>
                    <Td fontSize="sm">{report.pdfReportKey}</Td>
                    <Td fontSize="sm">{new Date(report.createdAt).toLocaleString()}</Td>
                    <Td>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => downloadReport(report.pdfReportKey)}
                        rightIcon={<ExternalLinkIcon />}
                      >
                        Last ned
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {historyResult.fetching && <Text>Laster rapporthistorikk...</Text>}
      </ContentBox>
    </Container>
  )
}
